import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { fileUploadService } from '@/lib/fileUploadService';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  placeholder?: string;
  className?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  placeholder = "Upload image or enter URL",
  className = ""
}) => {
  const MAX_FILE_SIZE_BYTES = 600 * 1024;
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(value || "");
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file upload
  const handleFileUpload = useCallback(async (file: File) => {
    if (!file) return;

    setErrorMessage("");

    if (!file.type.startsWith("image/")) {
      setErrorMessage("Only image files are allowed for article images.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrorMessage("Image size must be below 600KB.");
      return;
    }

    setIsUploading(true);

    try {
      // Use backend upload service
      const result = await fileUploadService.uploadFile(file);
      setPreviewUrl(result.url);
      onChange(result.url);
      
    } catch (error) {
      console.error('Upload failed:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [MAX_FILE_SIZE_BYTES, onChange]);

  // Handle drag events
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setErrorMessage("");

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      handleFileUpload(imageFile);
      return;
    }

    setErrorMessage("Please drop an image file (JPG, PNG, GIF, or WebP).");
  }, [handleFileUpload]);

  // Handle file input change
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage("");
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  // Handle URL input change
  const handleUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setErrorMessage("");
    setPreviewUrl(url);
    onChange(url);
  }, [onChange]);

  // Clear image
  const clearImage = useCallback(() => {
    // Cleanup and delete persisted uploads when possible
    if (previewUrl && !previewUrl.startsWith('blob:')) {
      fileUploadService.deleteFile(previewUrl);
    }
    
    // Cleanup object URL if it exists
    if (previewUrl && previewUrl.startsWith('blob:')) {
      fileUploadService.revokeUrl(previewUrl);
    }
    
    setPreviewUrl("");
    setErrorMessage("");
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [onChange, previewUrl]);

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-4 transition-colors
          ${isDragging ? 'border-primary bg-primary/5' : 'border-border'}
          ${isUploading ? 'opacity-50 pointer-events-none' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isUploading}
        />
        
        <div className="flex flex-col items-center justify-center text-center">
          {isUploading ? (
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
          ) : (
            <Upload className="w-8 h-8 text-muted-foreground mb-2" />
          )}
          
          <p className="text-sm font-medium">
            {isUploading ? 'Uploading...' : 'Drag & drop image here'}
          </p>
          <p className="text-xs text-muted-foreground">
            or click to browse
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            JPG, PNG, GIF up to 600KB
          </p>
        </div>
      </div>
      {errorMessage && (
        <p className="text-xs text-red-600 font-medium">{errorMessage}</p>
      )}

      {/* URL Input */}
      <div>
        <label className="text-xs font-medium text-muted-foreground">Or enter image URL</label>
        <div className="flex gap-2 mt-1">
          <input
            type="url"
            value={value || ""}
            onChange={handleUrlChange}
            placeholder="https://example.com/image.jpg"
            className="flex-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
          />
          {previewUrl && (
            <button
              onClick={clearImage}
              className="p-2 rounded-lg border border-input bg-background hover:bg-muted"
              title="Clear image"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Preview */}
      {previewUrl && (
        <div>
          <label className="text-xs font-medium text-muted-foreground">Preview</label>
          <div className="mt-1 relative">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-32 object-cover rounded-lg"
              onError={(e) => {
                e.currentTarget.src = "/placeholder.svg";
              }}
            />
            <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
              Preview
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
